import { createPath, resolvePath } from "react-router";
import type { Location, To } from "react-router";

// True when a path-like `to` resolves to the current location. The target is
// resolved against the current pathname (React Router style), so query-only or
// relative targets like "?term=x" (e.g. from the search() action) — and object
// targets like { pathname, search } — are recognised as the same location
// instead of slipping past the guard and re-triggering a same-location
// ROUTE_CHANGED loop. Comparison is on the full pathname+search+hash, so a
// navigation that genuinely changes any part — e.g. one that only clears the
// current hash — still runs. Numeric history deltas (goBack/go) and other
// non-path-like values are never same-location no-ops, so they bypass the
// check.
//
// Kept as a standalone pure helper (no router import) so both navigation entry
// points can share it without a circular dependency: navigation.ts passes the
// router singleton's location, while RouteElement passes its useLocation()
// value.
export const isSameLocation = (
  to: unknown,
  location: Pick<Location, "pathname" | "search" | "hash">
): boolean => {
  const isPathLike =
    typeof to === "string" || (typeof to === "object" && to !== null);
  if (!isPathLike) return false;
  return (
    createPath(resolvePath(to as To, location.pathname)) ===
    createPath(location)
  );
};
